import * as moment from 'moment';
import { tdb as db } from '../database';
import { encrypt_item, decrypt_item } from '../support/crypt';

export const TopicCacheRecordSchema = '[key+version+language], accessed';

export class TopicCacheRecord {
	constructor(
		public key: string,
		public version: string,
		public language: string,
		public topic: string,
		public related: string[],
		public accessed?: string
	) {
		//
	}

	public async save(): Promise<void> {
		this.accessed = moment().toISOString();
		await db.topiccache.put(this);
	}

	public age(): number {
		const { accessed } = this;
		if (accessed) {
			const now = moment();
			const old = moment(accessed);
			return now.diff(old, 'hours');
		}
		return 0;
	}
}

/**
 * Get topic using delayed read (cache-first) where possible.
 */
export async function get_topic({
	key,
	version,
	language,
	topic_getter,
	related_getter
}: {
	key: string;
	version: string;
	language: string;
	topic_getter: (params: { key: string; version: string; language: string }) => Promise<any>;
	related_getter?: (params: { key: string; version: string; language: string }) => Promise<string[]>;
}): Promise<Record<string, any>> {
	let record = await db.topiccache.where({ key, version, language }).first();
	if (record === undefined) {
		const topic = await topic_getter({ key, version, language });
		const related = related_getter ? await related_getter({ key, version, language }) : [];
		record = new TopicCacheRecord(key, version, language, await encrypt_item(topic), related);
	}
	// changes the accessed value
	record.save();
	const topic: { [key: string]: any } = await decrypt_item<any>(record.topic);
	return topic;
}

/**
 * Get related topics array from cache.
 */
export async function get_related({
	key,
	version,
	language,
	related_getter
}: {
	key: string;
	version: string;
	language: string;
	related_getter: (params: { key: string; version: string; language: string }) => Promise<string[]>;
}): Promise<string[]> {
	const record = await db.topiccache.where({ key, version, language }).first();
	if (record === undefined) {
		const related = await related_getter({ key, version, language });
		return related;
	} else if (record.related.length === 0) {
		record.related = await related_getter({ key, version, language });
		record.save();
	}
	return record.related;
}

/**
 * Cache a list of topics.
 */
export async function precache_topics({
	keys,
	version,
	language,
	topic_getter,
	related_getter
}: {
	keys: string[];
	version: string;
	language: string;
	topic_getter: (params: { key: string; version: string; language: string }) => Promise<any>;
	related_getter: (params: { key: string; version: string; language: string }) => Promise<string[]>;
}): Promise<any> {
	const alltopics = keys.map(async (key) => {
		let record = await db.topiccache.where({ key, version, language }).first();
		if (record === undefined) {
			const topic = await topic_getter({ key, version, language });
			const related = await related_getter({ key, version, language });
			record = new TopicCacheRecord(key, version, language, await encrypt_item(topic), related);
			record.save();
		}
		return record;
	});
	return await Promise.all(alltopics);
}

/**
 * Determine if a topic is currently cached.
 */
export async function has_topic({
	key,
	version,
	language
}: {
	key: string;
	version: string;
	language: string;
}): Promise<boolean> {
	const record = await db.topiccache.where({ key, version, language }).first();
	return record !== undefined;
}
